<template>
  <div class="bind-wallet-page">
    <!-- ===== 顶部标题 ===== -->
    <div class="bind-header">
      <h1 class="bind-title">{{ $t("bindAddress.title") }}</h1>
      <p class="bind-desc">{{ $t("bindAddress.desc") }}</p>
    </div>


    <!-- ===== 主体 ===== -->
    <div class="bind-body">
      <div class="bind-container">
        <!-- ===== 绑定表单 ===== -->
        <div class="bind-card">
          <h3 class="bind-card-title">{{ $t("bindAddress.cardTitle") }}</h3>

          <!-- 币种 -->
          <div class="form-group">
            <label class="form-label">{{ $t("bindAddress.selectCoin") }}</label>

            <div class="coin-grid">
              <div
                class="coin-option"
                v-for="coin in coins"
                :key="coin.value"
                :class="{ active: selectedCoin === coin.value }"
                @click="selectedCoin = coin.value"
              >
                <span class="coin-icon">{{ coin.icon }}</span>
                <span class="coin-name">{{ coin.value }}</span>
              </div>
            </div>
          </div>

          <!-- 协议 -->
          <div class="form-group" v-if="selectedCoin">
            <label class="form-label">{{ $t("bindAddress.selectProtocol") }}</label>

            <div class="protocol-grid">
              <div
                class="protocol-option"
                v-for="protocol in filteredProtocols"
                :key="protocol.p_id"
                :class="{ active: selectedProtocol === protocol.p_id }"
                @click="selectedProtocol = protocol.p_id"
              >
                <span class="protocol-name">{{ protocol.network }}</span>
                <span class="protocol-sub">{{ protocol.name }}</span>
              </div>
            </div>
          </div>

          <!-- 钱包地址 -->
          <div class="form-group">
            <label class="form-label">{{ $t("bindAddress.walletAddress") }}</label>
            <input
              type="text"
              class="form-input"
              v-model="walletAddress"
              :placeholder="$t('bindAddress.walletAddressPlaceholder')"
            />
          </div>

          <!-- 邮箱验证码 -->
          <div class="form-group">
            <label class="form-label">{{ $t("bindAddress.emailCode") }}</label>
            <div class="captcha-row">
              <input
                type="text"
                class="form-input captcha-input"
                v-model="emailCode"
                :placeholder="$t('bindAddress.emailCodePlaceholder')"
                maxlength="6"
              />

              <button
                class="btn-captcha"
                @click="openTwoFactorModal"
                :disabled="codeCountdown > 0"
              >
                {{
                  codeCountdown > 0
                    ? $t("bindAddress.resend", { count: codeCountdown })
                    : $t("bindAddress.sendCode")
                }}
              </button>
            </div>
          </div>

          <button
            class="btn-bind"
            @click="confirmBind"
            :disabled="!isFormValid"
          >
            {{ $t("bindAddress.confirmBind") }}
          </button>
        </div>

        <!-- ===== 注意事项 ===== -->
        <div class="notice-section">
          <div class="notice-header">
            <span class="notice-title">{{ $t("bindAddress.noticeTitle") }}</span>
          </div>

          <div class="notice-grid">
            <div class="notice-item" v-for="item in notices" :key="item.id">
              <span class="notice-icon">{{ item.icon }}</span>
              <div class="notice-content">
                <h4>{{ item.title }}</h4>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 两步验证弹窗（独立组件） ===== -->
    <TwoFactorModal
      ref="twoFactorModal"
      :desc="$t('bindAddress.twoFactorDesc')"
      @confirm="onTwoFactorConfirm"
    />

  </div>
</template>

<script>
import TwoFactorModal from "@/components/TwoFactorModal.vue";

export default {
  name: "BindWalletPage",
  components: {
    TwoFactorModal,
  },
  data() {
    return {
      // 表单数据
      selectedCoin: null,
      selectedProtocol: null,
      walletAddress: "",
      emailCode: "",
      isEmailCodeSent: false,

      // 验证码倒计时
      codeCountdown: 0,
      codeTimer: null,

      // 币种列表（从接口获取）
      coins: [],

      notices: [
        {
          id: 1,
          icon: "🔒",
          title: this.$t("bindAddress.notice1Title"),
          desc: this.$t("bindAddress.notice1Desc"),
        },
        {
          id: 2,
          icon: "🔄",
          title: this.$t("bindAddress.notice2Title"),
          desc: this.$t("bindAddress.notice2Desc"),
        },
        {
          id: 3,
          icon: "📧",
          title: this.$t("bindAddress.notice3Title"),
          desc: this.$t("bindAddress.notice3Desc"),
        },
        {
          id: 4,
          icon: "⚠️",
          title: this.$t("bindAddress.notice4Title"),
          desc: this.$t("bindAddress.notice4Desc"),
        },
      ],

    };
  },

  computed: {
    filteredProtocols() {
      const coin = this.coins.find((c) => c.value === this.selectedCoin);
      return coin ? coin.protocol || [] : [];
    },

    isFormValid() {
      return (
        this.selectedCoin &&
        this.selectedProtocol &&
        this.walletAddress.trim().length > 10 &&
        this.emailCode.trim().length === 4
      );
    },
  },

  watch: {
    selectedCoin() {
      const protocols = this.filteredProtocols;
      this.selectedProtocol = protocols.length ? protocols[0].p_id : null;
    },
  },

  created() {
    // 恢复邮箱验证码已发送状态：只要通过两步验证并发送了邮箱码，刷新后仍可输入
    if (localStorage.getItem("bind_email_code_sent") === "1") {
      this.isEmailCodeSent = true;
    }
    this.getCurrencyItemList();
  },

  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer);
    }
  },
  methods: {
    getCurrencyItemList() {
      this.$api.chain.getCurrencyItemList().then((res) => {
        const list = res.data || [];
        // 按币种名称分组
        const coinMap = {};
        list.forEach((item) => {
          if (!coinMap[item.name]) {
            coinMap[item.name] = {
              value: item.name,
              c_id: item.c_id,
              icon: this.coinIcon(item.name),
              protocol: [],
            };
          }
          coinMap[item.name].protocol.push({
            p_id: item.p_id,
            network: item.network,
            name: item.name,
          });
        });
        this.coins = Object.values(coinMap);
        if (this.coins.length) {
          this.selectedCoin = this.coins[0].value;
        }
      });
    },
    coinIcon(text) {
      if (!text) return "🪙";
      const map = {
        BTC: "₿",
        ETH: "⟠",
        TRX: "T",
        USDT: "₮",
        USDC: "$",
      };
      return map[text.toUpperCase()] || text.charAt(0).toUpperCase();
    },

    // ============================================================
    // 1. 点击「发送验证码」→ 打开两步验证弹窗
    // ============================================================
    openTwoFactorModal() {
      // 校验钱包地址是否已填写
      if (!this.walletAddress || this.walletAddress.trim().length < 10) {
        alert(this.$t("bindAddress.msgAddressInvalid"));
        return;
      }


      // 打开两步验证弹窗（组件内部会重置状态并聚焦）
      this.$refs.twoFactorModal.open();
    },

    // ============================================================
    // 2. 两步验证通过 → 发送邮箱验证码
    // ============================================================
    onTwoFactorConfirm(twoCode) {
      // 设置按钮为验证中状态
      this.$refs.twoFactorModal.setVerifying(true);

      // 调用发送邮箱验证码接口
      this.$api.code
        .sendSafetyCode({
          scene: "bind_address",
          twoCode,
        })
        .then((res) => {
          if (res.code == 1) {
            // 验证失败，显示错误并重置
            this.$refs.twoFactorModal.setError(
              res.msg || this.$t("bindAddress.msgCodeError"),
            );

            return;
          }
          this.$message.success(res.msg);
          this.$refs.twoFactorModal.close();
          this.sendEmailCode();
        });
    },

    // ============================================================
    // 3. 发送邮箱验证码接口
    // ============================================================
    async sendEmailCode() {
      if (this.codeCountdown > 0) return;

      // 模拟调用发送邮箱验证码接口
      console.log("📧 调用发送邮箱验证码接口");
      console.log("   → 邮箱: user@example.com");
      console.log("   → 钱包地址:", this.walletAddress);
      console.log("   → 币种:", this.selectedCoin);
      console.log("   → 协议:", this.selectedProtocol);

      // 实际场景中，这里会调用后端 API
      // const res = await this.$api.code.sendSafetyCode({
      //   scene: "bind_address",
      // });

      // if (res.code == 1) {
      //   this.$message.warning(res.msg);
      //   return false;
      // }

      // this.$message.success(res.msg);

      // 模拟发送成功
      this.isEmailCodeSent = true;
      // 持久化：只要通过两步验证并发送了邮箱码，刷新后仍可输入
      localStorage.setItem("bind_email_code_sent", "1");

      // 开始倒计时
      this.codeCountdown = 60;
      this.codeTimer = setInterval(() => {
        this.codeCountdown--;
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer);
          this.codeTimer = null;
          // 倒计时结束不再重置 isEmailCodeSent，保持可输入状态
        }
      }, 1000);

      return true;
    },

    // ============================================================
    // 5. 确认绑定
    // ============================================================
    async confirmBind() {
      if (!this.isFormValid) {
        alert(this.$t("bindAddress.msgFormInvalid"));
        return;
      }


      console.log("🔗 绑定钱包:", {
        coin: this.selectedCoin,
        protocol: this.selectedProtocol,
        address: this.walletAddress,
        emailCode: this.emailCode,
      });

      // 保存地址：需要协议 p_id
      // const coin = this.coins.find((c) => c.value === this.selectedCoin);
      // const protocol = this.filteredProtocols.find(
      //   (p) => p.p_id === this.selectedProtocol,
      // );

      const res = await this.$api.userWallet.saveAddress({
        code: this.emailCode,
        c_id: this.selectedProtocol,
        address: this.walletAddress,
      });
      if (res.code == 1) {
        this.$message.warning(res.msg);
        return false;
      }
      this.$message.success(res.msg);

      // 重置表单
      this.walletAddress = "";
      this.emailCode = "";
      this.isEmailCodeSent = false;
      // 绑定成功后清除持久化状态，下次绑定需重新验证
      localStorage.removeItem("bind_email_code_sent");
      // 重置倒计时
      if (this.codeTimer) {
        clearInterval(this.codeTimer);
        this.codeTimer = null;
      }
      this.codeCountdown = 0;

      setTimeout(() => {
        this.$router.push({ name: "Member", query: { tab: "addresses" } });
      }, 1500);
      // alert("✅ 钱包绑定成功！\n注意：绑定后 24 小时内不可提现");
    },
  },
};
</script>

<style scoped>
/* ============================================================
   绑定钱包页面
   ============================================================ */
.bind-wallet-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial,
    sans-serif;
  background: #0a0e1a;
  color: #fff;
  min-height: 100vh;
  padding: 24px 20px;
}

/* ===== 顶部 ===== */
.bind-header {
  text-align: center;
  padding: 16px 0 20px;
}

.bind-title {
  font-size: 30px;
  font-weight: 800;
  margin: 0;
}

.bind-title .highlight {
  background: linear-gradient(135deg, #00b4ff, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.bind-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  margin: 4px 0 0;
}

/* ===== 容器 ===== */
.bind-container {
  max-width: 900px;
  margin: 0 auto;
}

/* ===== 已绑定钱包列表 ===== */
.wallet-list {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 18px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.list-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
}

.list-count {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.03);
  padding: 2px 12px;
  border-radius: 12px;
}

.wallet-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.01);
  border-radius: 8px;
  margin-bottom: 6px;
  transition: background 0.2s;
}

.wallet-item:last-child {
  margin-bottom: 0;
}

.wallet-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.wallet-item-left {
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.wallet-item-icon {
  font-size: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  flex-shrink: 0;
}

.wallet-item-coin {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}

.wallet-item-address {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
  font-family: "SF Mono", monospace;
  word-break: break-all;
}

.wallet-item-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.wallet-item-status {
  font-size: 12px;
}

.wallet-item-status.verified {
  color: #00c951;
}

.wallet-item-status.pending {
  color: #ffd866;
}

.btn-delete {
  padding: 4px 8px;
  border-radius: 6px;
  background: rgba(255, 0, 0, 0.04);
  border: none;
  color: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-delete:hover {
  background: rgba(255, 0, 0, 0.1);
  color: #ff6b6b;
}

/* ===== 绑定卡片 ===== */
.bind-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 18px;
}

.bind-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 16px 0;
}

/* ===== 表单 ===== */
.form-group {
  margin-bottom: 14px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
}

/* ===== 币种 ===== */
.coin-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.coin-option {
  padding: 12px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px solid rgba(255, 255, 255, 0.04);
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.coin-option:hover {
  border-color: rgba(255, 255, 255, 0.08);
}

.coin-option.active {
  border-color: #00b4ff;
  background: rgba(0, 180, 255, 0.06);
}

.coin-icon {
  display: block;
  font-size: 22px;
}

.coin-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.coin-option.active .coin-name {
  color: #fff;
}

/* ===== 协议 ===== */
.protocol-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.protocol-option {
  padding: 10px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px solid rgba(255, 255, 255, 0.04);
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.protocol-option:hover {
  border-color: rgba(255, 255, 255, 0.08);
}

.protocol-option.active {
  border-color: #7c3aed;
  background: rgba(124, 58, 237, 0.06);
}

.protocol-name {
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
}

.protocol-option.active .protocol-name {
  color: #fff;
}

.protocol-sub {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.2);
}

/* ===== 输入框 ===== */
.form-input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.25s ease;
}

.form-input:focus {
  border-color: #00b4ff;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.15);
}

.form-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== 验证码行 ===== */
.captcha-row {
  display: flex;
  gap: 10px;
}

.captcha-input {
  flex: 1;
}

.btn-captcha {
  padding: 12px 20px;
  border-radius: 10px;
  background: rgba(0, 180, 255, 0.06);
  border: 1px solid rgba(0, 180, 255, 0.06);
  color: #00b4ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-captcha:hover:not(:disabled) {
  background: rgba(0, 180, 255, 0.12);
}

.btn-captcha:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== 绑定按钮 ===== */
.btn-bind {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
}

.btn-bind:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(0, 100, 255, 0.25);
}

.btn-bind:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ============================================================
   注意事项
   ============================================================ */
.notice-section {
  padding: 18px 22px 22px;
  background: rgba(255, 215, 0, 0.02);
  border: 1px solid rgba(255, 215, 0, 0.06);
  border-radius: 12px;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.notice-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
}

.notice-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
}

.notice-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.01);
  border-radius: 8px;
  transition: background 0.2s;
}

.notice-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.notice-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.notice-content h4 {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 2px 0;
}

.notice-content p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
  margin: 0;
  line-height: 1.5;
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 768px) {
  .bind-wallet-page {
    padding: 16px 12px;
  }

  .bind-title {
    font-size: 24px;
  }

  .coin-grid,
  .protocol-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .captcha-row {
    flex-direction: column;
  }

  .btn-captcha {
    width: 100%;
    justify-content: center;
  }

  .notice-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .notice-section {
    padding: 14px 16px 18px;
  }

  .notice-item {
    padding: 8px 10px;
  }

  .wallet-item {
    flex-wrap: wrap;
    gap: 8px;
  }

  .wallet-item-right {
    width: 100%;
    justify-content: flex-end;
  }

  .bind-card {
    padding: 16px;
  }

  .wallet-item-address {
    font-size: 11px;
  }
}

@media (max-width: 400px) {
  .coin-grid,
  .protocol-grid {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }

  .coin-option,
  .protocol-option {
    padding: 8px 6px;
  }

  .wallet-item-address {
    font-size: 10px;
  }
}
</style>
